import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  Car, 
  MapPin, 
  CreditCard,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  User,
  Gift,
  Star,
  Copy,
  Share2,
  Users,
  Navigation,
  Eye
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { 
    label: 'Pending', 
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    icon: Clock
  },
  confirmed: { 
    label: 'Confirmed', 
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: CheckCircle
  },
  in_progress: { 
    label: 'In Progress', 
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    icon: Car
  },
  completed: { 
    label: 'Completed', 
    color: 'bg-green-50 text-green-700 border-green-200',
    icon: CheckCircle
  },
  cancelled: { 
    label: 'Cancelled', 
    color: 'bg-red-50 text-red-700 border-red-200',
    icon: XCircle
  }
};

const paymentStatusConfig = {
  paid: { label: 'Paid', color: 'text-green-600' },
  unpaid: { label: 'Unpaid', color: 'text-gray-500' },
  refunded: { label: 'Refunded', color: 'text-orange-600' }
};

const workshopStatusConfig = {
  pending: { label: 'Booked', color: 'bg-gray-100 text-gray-700' },
  checked_in: { label: 'Checked In', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  ready_for_pickup: { label: 'Ready for Pickup', color: 'bg-emerald-100 text-emerald-700' }
};

export default function MyBookings() {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [applyingReferral, setApplyingReferral] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      fetchBookings();
      fetchLoyaltyStatus();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my-bookings`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLoyaltyStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/loyalty/full-status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLoyaltyStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch loyalty status:', error);
    }
  };

  const copyReferralCode = () => {
    if (loyaltyStatus?.referral_code) {
      navigator.clipboard.writeText(loyaltyStatus.referral_code);
      toast.success('Referral code copied!');
    }
  };

  const shareReferral = () => {
    if (navigator.share && loyaltyStatus) {
      navigator.share({
        title: 'MA Africa Fix & Detail',
        text: `Use my referral code ${loyaltyStatus.referral_code} and we both get a bonus stamp towards a FREE Full Valet!`,
        url: loyaltyStatus.referral_link
      });
    } else {
      copyReferralCode();
    }
  };

  const applyReferralCode = async () => {
    if (!referralCodeInput.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    
    setApplyingReferral(true);
    try {
      const response = await fetch(`${API_URL}/api/referral/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ referral_code: referralCodeInput.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setReferralCodeInput('');
        fetchLoyaltyStatus();
      } else {
        toast.error(data.detail || 'Failed to apply referral code');
      }
    } catch (error) {
      toast.error('Failed to apply referral code');
    } finally {
      setApplyingReferral(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr + 'T12:00:00');
      return date.toLocaleDateString('en-ZA', { 
        weekday: 'short',
        day: 'numeric', 
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (isoStr) => {
    try {
      const date = new Date(isoStr);
      return date.toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoStr;
    }
  };

  const isUpcoming = (dateStr) => {
    try {
      const bookingDate = new Date(dateStr + 'T23:59:59');
      return bookingDate >= new Date();
    } catch {
      return false;
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center pt-20">
        <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
      </div>
    );
  }

  // Require login
  if (!user) {
    return (
      <main data-testid="my-bookings-login-required" className="bg-white min-h-screen pt-20">
        <div className="max-w-[480px] mx-auto px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-20 h-20 rounded-full bg-[#C41E3A]/10 flex items-center justify-center mx-auto mb-6">
              <User className="w-10 h-10 text-[#C41E3A]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold text-[#1d1d1f] tracking-tight mb-3">
              Sign in to view bookings
            </h1>
            <p className="text-base text-[#86868b] mb-8 max-w-sm mx-auto">
              Please sign in to view your booking history.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 bg-[#C41E3A] text-white px-8 py-3 rounded-xl font-medium hover:bg-[#a01830] transition-colors"
                data-testid="login-to-view-btn"
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#C41E3A] text-[#C41E3A] px-8 py-3 rounded-xl font-medium hover:bg-[#C41E3A]/5 transition-colors"
                data-testid="signup-to-view-btn"
              >
                Sign Up
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
    );
  }

  const upcomingBookings = bookings.filter(b => isUpcoming(b.preferred_date) && b.status !== 'cancelled');
  const pastBookings = bookings.filter(b => !isUpcoming(b.preferred_date) || b.status === 'cancelled');

  return (
    <main data-testid="my-bookings-page" className="bg-white min-h-screen pt-20 pb-16">
      <div className="max-w-[800px] mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="py-8 md:py-12"
        >
          <h1 className="text-3xl md:text-4xl font-semibold text-[#1d1d1f] tracking-tight mb-2">
            My Bookings
          </h1>
          <p className="text-base text-[#86868b]">
            View and manage your detailing appointments
          </p>
        </motion.div>

        {/* Loyalty Program Card */}
        {loyaltyStatus && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
            data-testid="loyalty-dashboard"
          >
            {/* Stamps Card */}
            <div className={`rounded-2xl p-6 mb-4 ${
              loyaltyStatus.has_reward_available 
                ? 'bg-gradient-to-br from-amber-50 to-yellow-100 border border-amber-200' 
                : 'bg-gradient-to-br from-[#f5f5f7] to-[#e8e8ed]'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {loyaltyStatus.has_reward_available ? (
                    <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                      <Gift className="w-6 h-6 text-white" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-[#C41E3A]/10 flex items-center justify-center">
                      <Star className="w-6 h-6 text-[#C41E3A]" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-[#1d1d1f]">Loyalty Rewards</h3>
                    <p className="text-xs text-[#86868b]">
                      {loyaltyStatus.has_reward_available 
                        ? `You have a FREE Full Valet available!` 
                        : `${loyaltyStatus.bookings_until_reward} more to FREE Full Valet`}
                    </p>
                  </div>
                </div>
                {loyaltyStatus.has_reward_available && (
                  <Link
                    to="/detail"
                    className="bg-amber-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-amber-600 transition-colors"
                  >
                    Redeem Now
                  </Link>
                )}
              </div>
              
              {/* Stamp Progress */}
              <div className="flex items-center gap-2 justify-center">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-12 h-12 rounded-xl flex items-center justify-center border-2 transition-all ${
                      i < loyaltyStatus.total_progress
                        ? 'bg-[#C41E3A] border-[#C41E3A] text-white'
                        : 'bg-white border-gray-200 text-gray-300'
                    }`}
                  >
                    {i < loyaltyStatus.total_progress ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      <Star className="w-5 h-5" />
                    )}
                  </div>
                ))}
                <div className="w-12 h-12 rounded-xl flex items-center justify-center border-2 border-dashed border-amber-400 bg-amber-50">
                  <Gift className="w-5 h-5 text-amber-500" />
                </div>
              </div>
              
              {/* Progress Text */}
              <p className="text-center text-xs text-[#86868b] mt-3">
                {loyaltyStatus.total_progress}/5 stamps collected
                {loyaltyStatus.bonus_stamps > 0 && (
                  <span className="text-amber-600 font-medium"> (+{loyaltyStatus.bonus_stamps} bonus)</span>
                )}
              </p>
            </div>

            {/* Referral Card */}
            <div className="bg-[#f5f5f7] rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1d1d1f] text-sm">Refer a Friend</h3>
                    <p className="text-xs text-[#86868b]">Both get a bonus stamp!</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2">
                    <code className="text-sm font-mono font-semibold text-[#1d1d1f]">
                      {loyaltyStatus.referral_code}
                    </code>
                    <button 
                      onClick={copyReferralCode}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-4 h-4 text-[#86868b]" />
                    </button>
                  </div>
                  <button
                    onClick={shareReferral}
                    className="p-2 bg-[#C41E3A] text-white rounded-lg hover:bg-[#a01830] transition-colors"
                    title="Share"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {loyaltyStatus.successful_referrals > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-[#86868b]">
                    <span className="font-medium text-[#1d1d1f]">{loyaltyStatus.successful_referrals}</span> successful referral{loyaltyStatus.successful_referrals !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
              
              {/* Apply Referral Code (for new users) */}
              {loyaltyStatus.total_completed_bookings === 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-[#86868b] mb-2">Have a friend&apos;s code? Apply it before your first booking:</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={referralCodeInput}
                      onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
                      placeholder="Enter code (e.g. MA1234)"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#C41E3A] focus:outline-none"
                      maxLength={8}
                    />
                    <button
                      onClick={applyReferralCode}
                      disabled={applyingReferral}
                      className="px-4 py-2 bg-[#1d1d1f] text-white rounded-lg text-sm font-medium hover:bg-[#2d2d2f] transition-colors disabled:opacity-50"
                    >
                      {applyingReferral ? '...' : 'Apply'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {bookings.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 rounded-full bg-[#f5f5f7] flex items-center justify-center mx-auto mb-6">
              <Calendar className="w-10 h-10 text-[#86868b]" />
            </div>
            <h2 className="text-xl font-semibold text-[#1d1d1f] mb-2">No bookings yet</h2>
            <p className="text-[#86868b] mb-8">Book your first Prestige Detail today!</p>
            <Link
              to="/booking"
              className="inline-flex items-center gap-2 bg-[#C41E3A] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#a01830] transition-colors"
              data-testid="book-first-btn"
            >
              Book Now
            </Link>
          </motion.div>
        ) : (
          <>
            {/* Upcoming Bookings */}
            {upcomingBookings.length > 0 && (
              <section className="mb-10">
                <h2 className="text-lg font-semibold text-[#1d1d1f] mb-4">Upcoming</h2>
                <div className="space-y-4">
                  {upcomingBookings.map((booking, index) => {
                    const StatusIcon = statusConfig[booking.status]?.icon || Clock;
                    const workshopStatus = booking.workshop_status || 'pending';
                    return (
                      <motion.div
                        key={booking.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white border border-[#d2d2d7] rounded-2xl p-5 hover:border-[#C41E3A]/30 transition-colors"
                        data-testid={`booking-${booking.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 cursor-pointer" onClick={() => setSelectedBooking(booking)}>
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusConfig[booking.status]?.color}`}>
                                <StatusIcon className="w-3 h-3" />
                                {statusConfig[booking.status]?.label || booking.status}
                              </span>
                              <span className={`text-xs font-medium ${paymentStatusConfig[booking.payment_status]?.color}`}>
                                {paymentStatusConfig[booking.payment_status]?.label}
                              </span>
                              {workshopStatus !== 'pending' && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${workshopStatusConfig[workshopStatus]?.color}`}>
                                  {workshopStatusConfig[workshopStatus]?.label}
                                </span>
                              )}
                            </div>
                            <h3 className="text-base font-semibold text-[#1d1d1f] mb-1">
                              {booking.package_name}
                            </h3>
                            <p className="text-sm text-[#86868b]">
                              {booking.vehicle_make} {booking.vehicle_model}
                            </p>
                            {booking.reference_number && (
                              <p className="text-xs text-[#86868b] mt-1">
                                Ref: {booking.reference_number}
                              </p>
                            )}
                          </div>
                          <div className="text-right flex flex-col items-end gap-2">
                            <p className="text-lg font-semibold text-[#C41E3A]">
                              R{booking.total_price?.toLocaleString()}
                            </p>
                            <p className="text-sm text-[#86868b]">
                              {formatDate(booking.preferred_date)}
                            </p>
                            <p className="text-xs text-[#86868b]">
                              {booking.preferred_time}
                            </p>
                            {booking.reference_number && (
                              <Link
                                to={`/track/${booking.reference_number}`}
                                onClick={(e) => e.stopPropagation()}
                                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#C41E3A] text-white text-xs font-medium rounded-full hover:bg-[#a01830] transition-colors"
                                data-testid={`track-btn-${booking.id}`}
                              >
                                <Eye className="w-3 h-3" />
                                Track
                              </Link>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Past Bookings */}
            {pastBookings.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-[#1d1d1f] mb-4">Past Bookings</h2>
                <div className="space-y-3">
                  {pastBookings.map((booking, index) => (
                    <motion.div
                      key={booking.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="bg-[#f5f5f7] rounded-xl p-4 hover:bg-[#e8e8ed] transition-colors"
                      data-testid={`past-booking-${booking.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="cursor-pointer flex-1" onClick={() => setSelectedBooking(booking)}>
                          <p className="text-sm font-medium text-[#1d1d1f]">{booking.package_name}</p>
                          <p className="text-xs text-[#86868b]">
                            {booking.vehicle_make} {booking.vehicle_model} • {formatDate(booking.preferred_date)}
                          </p>
                          {booking.reference_number && (
                            <p className="text-xs text-[#86868b] mt-0.5">Ref: {booking.reference_number}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium ${statusConfig[booking.status]?.color} px-2 py-0.5 rounded-full`}>
                            {statusConfig[booking.status]?.label}
                          </span>
                          {booking.reference_number && (
                            <Link
                              to={`/track/${booking.reference_number}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-200 text-zinc-700 text-xs font-medium rounded-full hover:bg-zinc-300 transition-colors"
                              data-testid={`track-past-btn-${booking.id}`}
                            >
                              <Eye className="w-3 h-3" />
                              Track
                            </Link>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* Book Again CTA */}
            <div className="mt-12 text-center">
              <Link
                to="/booking"
                className="inline-flex items-center gap-2 text-[#C41E3A] hover:text-[#a01830] font-medium"
                data-testid="book-again-btn"
              >
                Book another detail <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        )}

        {/* Booking Detail Modal */}
        {selectedBooking && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedBooking(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="booking-detail-modal"
            >
              <div className="p-6 border-b border-[#d2d2d7]">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[#1d1d1f]">Booking Details</h2>
                  <button
                    onClick={() => setSelectedBooking(null)}
                    className="text-[#86868b] hover:text-[#1d1d1f] text-2xl leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${statusConfig[selectedBooking.status]?.color}`}>
                    {statusConfig[selectedBooking.status]?.label || selectedBooking.status}
                  </span>
                  <span className={`text-sm font-medium ${paymentStatusConfig[selectedBooking.payment_status]?.color}`}>
                    {paymentStatusConfig[selectedBooking.payment_status]?.label}
                  </span>
                </div>

                {/* Service */}
                <div className="bg-[#f5f5f7] rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-[#1d1d1f] mb-3">Service</h3>
                  <p className="text-base font-medium text-[#1d1d1f]">{selectedBooking.package_name}</p>
                  <div className="flex items-center gap-2 text-sm text-[#424245] mt-2">
                    <Car className="w-4 h-4" />
                    {selectedBooking.vehicle_make} {selectedBooking.vehicle_model} ({selectedBooking.vehicle_type})
                  </div>
                </div>

                {/* Date & Time */}
                <div className="bg-[#f5f5f7] rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-[#1d1d1f] mb-3">Appointment</h3>
                  <div className="flex items-center gap-2 text-sm text-[#424245]">
                    <Calendar className="w-4 h-4" />
                    {formatDate(selectedBooking.preferred_date)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#424245] mt-2">
                    <Clock className="w-4 h-4" />
                    {selectedBooking.preferred_time}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#424245] mt-2">
                    <MapPin className="w-4 h-4" />
                    {selectedBooking.service_location === 'shop' ? 'Germiston Studio' : 'Mobile Service'}
                  </div>
                </div>

                {/* Payment */}
                <div className="bg-[#1d1d1f] rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#86868b]">Total Amount</p>
                      <p className="text-2xl font-bold text-[#C41E3A]">
                        R{selectedBooking.total_price?.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[#86868b]">Deposit Paid</p>
                      <p className="text-lg font-semibold text-white">
                        R{selectedBooking.deposit_amount?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Booking ID */}
                <div className="text-center text-xs text-[#86868b]">
                  <p>Booking ID: {selectedBooking.id?.slice(0, 8)}</p>
                  <p>Booked on: {formatDateTime(selectedBooking.created_at)}</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </main>
  );
}
